/**
 * /invite/:token — Public invitation acceptance page.
 *
 * Phase 2J-AK: New team members click the link in their invite email,
 * land here, see a tiny preview of who invited them + what role they're
 * being granted, then choose their password. On success we drop their
 * fresh JWT into localStorage and redirect them straight into the
 * admin/lab portal.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PasswordRules from "@/components/PasswordRules";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { acceptInvite } = useAuth();

  const [loading, setLoading]   = useState(true);
  const [invite, setInvite]     = useState(null);
  const [error, setError]       = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/auth/invitations/${token}`);
        if (!cancelled) setInvite(data);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e.response?.data?.detail) || "This invitation link is no longer valid.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (ev) => {
    ev.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const user = await acceptInvite(token, password);
      toast.success("Welcome aboard!");
      // Drop the user into the appropriate portal landing
      const role = user.role;
      if (role === "admin") navigate("/admin");
      else if (role === "lab_technician") navigate("/admin/lab");
      else if (role === "trade_partner") navigate("/trade");
      else navigate("/customer");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
      <div data-testid="invite-accept-card" className="w-full max-w-md bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="bg-slate-900 text-white p-7">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-cyan-300">Aware Water Testing</div>
          <h1 className="font-display text-2xl font-bold mt-2">Set up your account</h1>
          <p className="text-sm text-slate-300 mt-2">
            Choose a password to activate your team-member access.
          </p>
        </div>

        {loading && (
          <div className="p-10 flex items-center justify-center text-slate-500" data-testid="invite-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Verifying invitation…
          </div>
        )}

        {!loading && error && (
          <div data-testid="invite-error" className="p-7 text-center">
            <AlertTriangle className="w-9 h-9 text-rose-500 mx-auto mb-3" />
            <h2 className="font-display text-lg font-bold text-slate-900 mb-1">Link no longer valid</h2>
            <p className="text-sm text-slate-600 mb-4">{error}</p>
            <p className="text-xs text-slate-500">Ask your admin to resend the invitation.</p>
            <button onClick={() => navigate("/signin/select")} className="mt-5 inline-flex items-center text-sm font-semibold text-cyan-700 hover:underline">
              Go to sign-in →
            </button>
          </div>
        )}

        {!loading && !error && invite && (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 text-sm">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-cyan-700 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-slate-900" data-testid="invite-recipient-name">{invite.full_name}</div>
                  <div className="text-xs text-slate-500" data-testid="invite-recipient-email">{invite.email}</div>
                  <div className="text-[11px] text-slate-600 mt-1.5">
                    Role: <span className="font-semibold text-slate-900" data-testid="invite-role-label">{invite.role}</span>
                  </div>
                  {invite.invited_by_name && (
                    <div className="text-[11px] text-slate-500">Invited by {invite.invited_by_name}</div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">New password</label>
              <input
                data-testid="invite-password-input"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
                placeholder="At least 8 characters"
              />
              <PasswordRules password={password} testIdPrefix="invite" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Confirm password</label>
              <input
                data-testid="invite-confirm-input"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
              />
            </div>

            <button
              type="submit"
              data-testid="invite-submit-password-btn"
              disabled={submitting}
              className="w-full px-5 py-3 rounded-full text-sm font-semibold bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-60 transition-colors"
            >
              {submitting ? "Activating…" : "Activate my account"}
            </button>

            <p className="text-[11px] text-slate-400 text-center mt-2">
              By continuing you agree to keep your login credentials secure.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
